import React from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material'
import { uniqBy } from 'lodash'
import { CardTitle } from 'common/components/card/Card'
import { darkBlue, mainBlue } from 'app/theme'
import { STATUS_TICKETS } from 'common/constants/Constants'
import { getTextColorByHexColor, PASTEL_COLOR } from 'common/utils/ColorUtil'
import { ticketsLabel } from './DevLotsUtils'

const KpiTile = ({ label, value, caption, color, tooltip = '' }) => (
    <Tooltip title={tooltip}>
        <Box sx={{ borderLeft: `4px solid ${color}`, backgroundColor: '#f5f7fa', borderRadius: '3px', p: '6px 10px' }}>
            <Typography sx={{ fontSize: 11, color: '#6b7893', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#1c2340', lineHeight: 1.2 }}>{value}</Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7893' }}>{caption}</Typography>
        </Box>
    </Tooltip>
)
KpiTile.propTypes = { label: PropTypes.string, value: PropTypes.number, caption: PropTypes.string, color: PropTypes.string, tooltip: PropTypes.string }

const LotsTile =({ label, tickets = [], color }) => (
    <KpiTile label={label} value={uniqBy(tickets, 'lot').length} caption={ticketsLabel(tickets.length)} color={color} />
)
LotsTile.propTypes = { label: PropTypes.string, tickets: PropTypes.arrayOf(PropTypes.shape({})), color: PropTypes.string }

// Nombre de lots par type, tickets de la recette actuelle, et répartition par statut des tickets des lots en cours
// (le détail de chaque statut est dans l'info-bulle du segment).
const DevStatsCard = ({ currentsTickets = [], toComeTickets = [], closedTickets = [], recetteTickets = [], nbToCome }) => {
    const statusCounts = STATUS_TICKETS
        .map(s => ({ ...s, count: currentsTickets.filter(t => t.status === s.value).length }))
        .filter(s => s.count > 0)

    return (
        <Card sx={{ height: '100%' }}>
            <CardTitle title='Statistiques' color={darkBlue} />
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={3}><LotsTile label='Lots en cours' tickets={currentsTickets} color={PASTEL_COLOR.orange} /></Grid>
                    <Grid size={3}><LotsTile label='Lots à venir' tickets={toComeTickets} color={PASTEL_COLOR.red} /></Grid>
                    <Grid size={3}><LotsTile label='Lots fermés' tickets={closedTickets} color={PASTEL_COLOR.green} /></Grid>
                    <Grid size={3}>
                        <KpiTile
                            label='Tickets en recette'
                            value={recetteTickets.length}
                            caption={Number.isFinite(nbToCome) ? `${nbToCome} à venir` : '—'}
                            color={mainBlue}
                            tooltip='Tickets à venir pour la prochaine recette (en attente de validation interne), comme dans l’écran Recette'
                        />
                    </Grid>
                </Grid>
                <Typography sx={{ fontSize: 12, color: '#6b7893', mt: 1.5, mb: 0.5 }}>Tickets des lots en cours, par statut</Typography>
                {statusCounts.length === 0 && <Typography sx={{ fontSize: 13, color: '#98a3b8' }}>Aucun ticket</Typography>}
                {statusCounts.length > 0 && (
                    <Box sx={{ display: 'flex', height: 22, borderRadius: '4px', overflow: 'hidden' }}>
                        {statusCounts.map(s => (
                            <Tooltip key={s.id} title={`${s.value} : ${s.count}`}>
                                <Box sx={{ flex: s.count, minWidth: 20, backgroundColor: s.color, color: getTextColorByHexColor(s.color), fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {s.count}
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}

DevStatsCard.propTypes = {
    currentsTickets: PropTypes.arrayOf(PropTypes.shape({})),
    toComeTickets: PropTypes.arrayOf(PropTypes.shape({})),
    closedTickets: PropTypes.arrayOf(PropTypes.shape({})),
    recetteTickets: PropTypes.arrayOf(PropTypes.shape({})),
    nbToCome: PropTypes.number,
}

export default DevStatsCard
